/**
 * useUserType — is this a PBX extension user, or a contact-centre agent?
 *
 * THE DEFECT
 * ----------
 * `users.user_type` ('pbx' | 'callcenter') has existed since provisioning
 * (provision.ts:531) and never reached the client. The agent workspace
 * therefore rendered ONE screen for both kinds of user, so a PBX extension
 * user was shown the ACD State toggle and campaign controls — neither of
 * which applies to someone who just has a desk phone.
 *
 * STATUS vs STATE — the distinction this hook exists to encode
 * ------------------------------------------------------------
 * KC2 (`kruptoconnect-agent`, the FreeSWITCH-based predecessor) models these
 * as two separate axes, and its cc_dashboard legend states both:
 *
 *   Agent STATUS — presence and break accounting. EVERY user has one.
 *     Available            — ready to receive queue calls
 *     Available On Demand  — state set to Idle once the call ends
 *     On Break             — logged in, won't receive queue calls
 *     Logged Out           — cannot receive queue calls
 *
 *   Agent STATE — may the ACD route a QUEUED call to me? CONTACT-CENTRE ONLY.
 *     Idle            — no calls given
 *     Waiting         — ready to receive calls
 *     Receiving       — queue call being offered
 *     In a queue call — currently on a queue call
 *
 * In KC2 the STATE is applied with
 * `callcenter_config agent set state <ext> Waiting|Idle` and every mutation of
 * it is gated on `cc_user` (dialer/hook.js:109, :589, :763). The STATUS is
 * not: `break_codes` are fetched for every user at login with no such gate,
 * because a PBX user takes a lunch break too and their break time is just as
 * reportable.
 *
 * Conflating them would take break codes away from PBX users — a capability
 * they genuinely have.
 *
 * Centralised here rather than read from the auth store at each call site, so
 * the default ('pbx' — the narrower set) is applied in exactly one place.
 */
import { useAuthStore } from '../store/authStore.js'

export type UserType = 'pbx' | 'callcenter'

export interface UserCapabilities {
  userType: UserType
  /** Contact-centre agent: takes queued calls, has ACD state and campaigns. */
  isCallCenter: boolean
  /** PBX user: an extension, direct dial, transfer, voicemail. No ACD. */
  isPbx: boolean
  /**
   * Show the ACD Ready/Idle STATE toggle.
   *
   * Requires telephony AND contact-centre: a PBX user has no queue to be
   * ready for, and a user without telephony has no endpoint at all.
   *
   * NOT break codes. STATUS (Available / on a break) and STATE (may the ACD
   * route me a queued call) are different things, and only STATE is
   * contact-centre specific — in KC2 it is literally
   * `callcenter_config agent set state ... Waiting|Idle`. A PBX extension
   * user takes breaks like anyone else and their break time is just as
   * reportable, so the status dropdown is shown to everyone.
   */
  showAcdControls: boolean
  /** Show campaign / dialer panels. Contact-centre only. */
  showCampaignControls: boolean
  /** Show the softphone. Any telephony-enabled user, of either kind. */
  showSoftphone: boolean
  /** Show queue status and wallboard-style figures. */
  showQueueStatus: boolean
}

export function useUserType(): UserCapabilities {
  const user = useAuthStore((s) => s.user)

  const allowTelephony = user?.allowTelephony === true
  // Anything other than an explicit 'callcenter' is treated as PBX. A session
  // issued before this field existed must not be granted the wider UI.
  const userType: UserType = user?.userType === 'callcenter' ? 'callcenter' : 'pbx'
  const isCallCenter = userType === 'callcenter'

  return {
    userType,
    isCallCenter,
    isPbx: !isCallCenter,
    showAcdControls: allowTelephony && isCallCenter,
    showCampaignControls: isCallCenter,
    showSoftphone: allowTelephony,
    showQueueStatus: isCallCenter,
  }
}
